'use client';

import React, { useRef, useEffect, useState } from 'react';
import { Linkedin, Instagram, ArrowUpRight } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';

interface TeamMember {
  id: string;
  name: string;
  role: string;
  specialty: string;
  bio: string;
  avatar: string;
  avatarAlt: string;
  projects: number;
  years: number;
  tags: string[];
  social: {linkedin?: string;instagram?: string;};
}

const team: TeamMember[] = [
{
  id: 'tm1',
  name: 'Nadia Okonkwo',
  role: 'Founder & Executive Producer',
  specialty: 'Brand Storytelling',
  bio: 'Built NASARC from a two-person edit suite to a 30-person studio. Nadia oversees every project\'s creative direction and client relationship from brief to delivery.',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d4b36233-1772143858492.png",
  avatarAlt: 'Confident African woman in her 30s, creative professional with natural hair and warm expression',
  projects: 247,
  years: 6,
  tags: ['Executive Production', 'Brand Strategy', 'Client Relations'],
  social: { linkedin: '#', instagram: '#' }
},
{
  id: 'tm2',
  name: 'Marcus Velde',
  role: 'Director of Photography',
  specialty: 'Cinematic Capture',
  bio: 'Former commercial DP with credits spanning three continents. Marcus leads every major production shoot and has operated in conditions ranging from Arctic tundra to Middle Eastern desert.',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_150cda2fc-1772251983032.png",
  avatarAlt: 'European male photographer in his 40s with rugged look, professional creative industry appearance',
  projects: 183,
  years: 5,
  tags: ['8K Capture', 'Drone Unit', 'Lighting Design'],
  social: { linkedin: '#', instagram: '#' }
},
{
  id: 'tm3',
  name: 'Yuna Park',
  role: 'Head of AI Production',
  specialty: 'AI-Enhanced Content',
  bio: 'Yuna built NASARC\'s proprietary AI pipeline from scratch — integrating generative tools into production workflows that cut timelines by 40% without sacrificing creative quality.',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_120f980d1-1763300650373.png",
  avatarAlt: 'Asian woman with glasses and professional appearance, technology creative director',
  projects: 94,
  years: 3,
  tags: ['Generative AI', 'Motion Design', 'Pipeline Architecture'],
  social: { linkedin: '#', instagram: '#' }
},
{
  id: 'tm4',
  name: 'Tarek Mansouri',
  role: 'Lead Colorist',
  specialty: 'Post-Production',
  bio: 'Certified DaVinci Resolve colorist with a background in feature film. Tarek\'s grades are the final signature on every NASARC deliverable — the reason our footage looks the way it does.',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_184e5f4b2-1763294080263.png",
  avatarAlt: 'Middle Eastern man in his 30s, thoughtful expression, creative professional in studio setting',
  projects: 201,
  years: 4,
  tags: ['DaVinci Resolve', 'HDR Grading', 'Sound Design'],
  social: { linkedin: '#' }
},
{
  id: 'tm5',
  name: 'Elise Fontaine',
  role: 'Creative Strategist',
  specialty: 'Campaign Strategy',
  bio: 'Elise bridges the gap between creative vision and business objective. She runs discovery, writes briefs, and ensures every project has a clear strategic spine before a single frame is shot.',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1ed080a45-1772208398102.png",
  avatarAlt: 'French woman in her late 20s with professional appearance, creative strategist with warm demeanor',
  projects: 112,
  years: 3,
  tags: ['Creative Strategy', 'Campaign Planning', 'Brand Consulting'],
  social: { linkedin: '#', instagram: '#' }
},
{
  id: 'tm6',
  name: 'Ravi Subramaniam',
  role: 'On-Site Production Manager',
  specialty: 'Location Production',
  bio: 'Ravi has produced on-location shoots in 28 countries. From permits to logistics to crew coordination, he makes impossible locations possible — on time and on budget.',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_167c14e98-1772624814714.png",
  avatarAlt: 'South Asian man in his 30s with professional casual style, operations manager appearance',
  projects: 138,
  years: 4,
  tags: ['Location Scouting', 'International Permits', 'Crew Management'],
  social: { linkedin: '#' }
}];


function TeamCard({ member, index }: {member: TeamMember;index: number;}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) {setVisible(true);observer.disconnect();}},
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`group relative rounded-2xl bg-studio-900 border border-studio-700/50 overflow-hidden card-hover transition-all duration-500 ${
      visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`
      }
      style={{ transitionDelay: `${index * 80}ms` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      
      {/* Avatar */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <AppImage
          src={member.avatar}
          alt={member.avatarAlt}
          fill
          className={`object-cover object-top transition-transform duration-700 ${hovered ? 'scale-105' : 'scale-100'}`}
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw" />
        
        <div className="absolute inset-0 bg-gradient-to-t from-studio-900 via-studio-900/20 to-transparent" />

        {/* Specialty badge */}
        <div className="absolute top-3 left-3 px-2.5 py-1 bg-studio-950/80 backdrop-blur-sm border border-studio-600/40 rounded-full">
          <span className="text-gold-400 text-[10px] font-medium">{member.specialty}</span>
        </div>

        {/* Social links — appear on hover */}
        <div
          className={`absolute top-3 right-3 flex items-center gap-1.5 transition-all duration-300 ${
          hovered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'}`
          }>
          
          {member.social.linkedin &&
          <a
            href={member.social.linkedin}
            className="w-8 h-8 rounded-lg bg-studio-950/80 backdrop-blur-sm border border-studio-600/40 flex items-center justify-center text-studio-300 hover:text-white hover:border-studio-500/60 transition-colors"
            aria-label={`${member.name} on LinkedIn`}
            target="_blank"
            rel="noopener noreferrer">
            
              <Linkedin size={13} />
            </a>
          }
          {member.social.instagram &&
          <a
            href={member.social.instagram}
            className="w-8 h-8 rounded-lg bg-studio-950/80 backdrop-blur-sm border border-studio-600/40 flex items-center justify-center text-studio-300 hover:text-white hover:border-studio-500/60 transition-colors"
            aria-label={`${member.name} on Instagram`}
            target="_blank"
            rel="noopener noreferrer">
            
              <Instagram size={13} />
            </a>
          }
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-display font-semibold text-base text-white leading-tight">{member.name}</h3>
          <ArrowUpRight
            size={14}
            className={`shrink-0 mt-0.5 transition-all duration-200 ${hovered ? 'text-gold-400' : 'text-studio-600'}`} />
          
        </div>
        <p className="text-gold-400 text-xs font-medium mb-2">{member.role}</p>
        <p className="text-studio-400 text-sm leading-relaxed mb-4 line-clamp-2">{member.bio}</p>

        {/* Stats */}
        <div className="flex items-center gap-4 mb-4">
          <div className="text-center">
            <p className="font-display font-bold text-lg text-white font-mono-data">{member.projects}</p>
            <p className="text-studio-500 text-[10px]">Projects</p>
          </div>
          <div className="w-px h-6 bg-studio-700" />
          <div className="text-center">
            <p className="font-display font-bold text-lg text-white font-mono-data">{member.years}</p>
            <p className="text-studio-500 text-[10px]">Yrs at NASARC</p>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1">
          {member.tags.map((tag) =>
          <span key={tag} className="px-2 py-0.5 bg-studio-800/60 border border-studio-700/30 rounded text-studio-400 text-[10px]">
              {tag}
            </span>
          )}
        </div>
      </div>
    </div>);

}

export default function TeamSection() {
  return (
    <section id="team" className="py-24 xl:py-32 scroll-mt-20 bg-studio-950" aria-label="Our team">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-12 2xl:px-16">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-gold-400 text-xs font-semibold tracking-[0.2em] uppercase mb-3">The People</p>
            <h2 className="font-display font-bold text-4xl md:text-5xl xl:text-6xl text-white leading-tight">
              The Crew Behind
              <br />
              <span className="gradient-text-gold">Every Frame</span>
            </h2>
          </div>
          <p className="text-studio-400 text-base max-w-sm leading-relaxed md:text-right">
            30 full-time creatives, operators, and strategists — each a specialist in their domain.
          </p>
        </div>

        {/* Team grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-6 gap-5 xl:gap-6">
          {team.map((member, i) =>
          <TeamCard key={member.id} member={member} index={i} />
          )}
        </div>

        {/* Hiring CTA */}
        <div className="mt-10 p-6 rounded-2xl bg-studio-900 border border-studio-700/50 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-white font-semibold text-base mb-0.5">We're growing the crew.</p>
            <p className="text-studio-400 text-sm">Open roles in production, post, and AI — for people who take craft seriously.</p>
          </div>
          <button className="shrink-0 flex items-center gap-2 px-5 py-2.5 bg-studio-800/60 border border-studio-600/50 text-studio-200 text-sm font-medium rounded-xl hover:text-white hover:border-studio-500/60 hover:bg-studio-700/60 transition-all duration-200 active:scale-95 whitespace-nowrap">
            View Open Roles
            <ArrowUpRight size={14} />
          </button>
        </div>
      </div>
    </section>);

}